/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { format } from "date-fns";

import {
  MdClose,
  MdAccessTime,
  MdLocationOn,
  MdPerson,
  MdPeople,
  MdVideoCall,
  MdEdit,
  MdDelete,
  MdCheck,
  MdHelpOutline,
} from "react-icons/md";
import { Tooltip } from "@mui/material";
import { CalendarEvent } from "../types";
import * as S from "../styles";

interface EventDetailPopupProps {
  event: CalendarEvent | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onEdit: (event: CalendarEvent) => void;
  onDelete: (eventId: string) => void;
  onAccept: (eventId: string) => void;
  onDecline: (eventId: string) => void;
  onTentative: (eventId: string) => void;
  currentUserEmail?: string;
}

const EventDetailPopup: React.FC<EventDetailPopupProps> = ({
  event,
  anchorEl,
  onClose,
  onEdit,
  onDelete,
  onAccept,
  onDecline,
  onTentative,
  currentUserEmail,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: -9999,
    left: -9999,
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (event) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [event, onClose]);

  // Reposiciona após renderizar, medindo a altura real do popup
  useLayoutEffect(() => {
    if (!event || !anchorEl || !popupRef.current) return;

    const rect = anchorEl.getBoundingClientRect();
    const popup = popupRef.current;
    const popupHeight = popup.offsetHeight;
    const popupWidth = 360;
    const margin = 8;

    // Horizontal: tenta à direita do anchor, senão à esquerda
    let calcLeft =
      rect.right + 10 + popupWidth > window.innerWidth - margin
        ? rect.left - popupWidth - 10
        : rect.right + 10;
    calcLeft = Math.max(
      margin,
      Math.min(calcLeft, window.innerWidth - popupWidth - margin)
    );

    // Vertical: tenta alinhar no topo do anchor, ajusta se ultrapassar
    let calcTop = rect.top;
    if (calcTop + popupHeight > window.innerHeight - margin) {
      calcTop = window.innerHeight - popupHeight - margin;
    }
    calcTop = Math.max(margin, calcTop);

    setPosition({ top: calcTop, left: calcLeft });
  }, [event, anchorEl]);

  if (!event || !anchorEl) return null;

  const organizerEmail = event.organizer?.emailAddress?.address?.toLowerCase();
  const isOrganizer =
    !!currentUserEmail &&
    !!organizerEmail &&
    currentUserEmail.toLowerCase() === organizerEmail;

  const startDate = event.start?.dateTime
    ? new Date(event.start.dateTime)
    : null;
  const endDate = event.end?.dateTime ? new Date(event.end.dateTime) : null;

  const meetingUrl =
    event.onlineMeeting?.joinUrl || event.onlineMeetingUrl || null;

  const getStatusColor = () => {
    switch (event.showAs) {
      case "busy":
        return "#3f51b5";
      case "tentative":
        return "#ff9800";
      case "free":
        return "#4caf50";
      case "oof":
        return "#e91e63";
      default:
        return "#2d5bff";
    }
  };

  return (
    <S.EventDetailPopup
      ref={popupRef}
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <S.EventDetailHeader $color={getStatusColor()}>
        <S.EventDetailTitle>
          {event.isCancelled ? "Cancelado: " : ""}
          {event.subject || "Sem título"}
        </S.EventDetailTitle>
        <S.EventDetailCloseButton onClick={onClose}>
          <MdClose size={16} />
        </S.EventDetailCloseButton>
      </S.EventDetailHeader>

      <S.EventDetailBody>
        {startDate && endDate && (
          <S.EventDetailRow>
            <MdAccessTime size={18} />
            <div>
              <div>
                {startDate.toLocaleDateString("pt-BR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                })}
              </div>
              <div style={{ fontSize: 12, marginTop: 2 }}>
                {event.isAllDay
                  ? "Dia inteiro"
                  : `${format(startDate, "HH:mm")} - ${format(
                      endDate,
                      "HH:mm"
                    )}`}
              </div>
            </div>
          </S.EventDetailRow>
        )}

        {event.location?.displayName && (
          <S.EventDetailRow>
            <MdLocationOn size={18} />
            <span>{event.location.displayName}</span>
          </S.EventDetailRow>
        )}

        {meetingUrl && (
          <S.EventDetailRow>
            <MdVideoCall size={18} />
            <S.EventDetailLink
              href={meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Participar da reunião do Teams
            </S.EventDetailLink>
          </S.EventDetailRow>
        )}

        {event.organizer?.emailAddress && (
          <S.EventDetailRow>
            <MdPerson size={18} />
            <div>
              <div style={{ fontWeight: 500 }}>Organizador</div>
              <div>
                {event.organizer.emailAddress.name ||
                  event.organizer.emailAddress.address}
              </div>
            </div>
          </S.EventDetailRow>
        )}

        {event.attendees && event.attendees.length > 0 && (
          <S.EventDetailRow>
            <MdPeople size={18} />
            <div>
              <div style={{ fontWeight: 500 }}>
                Participantes ({event.attendees.length})
              </div>
              {event.attendees.slice(0, 5).map((att, i) => (
                <div key={i} style={{ fontSize: 12, marginTop: 2 }}>
                  {att.emailAddress?.name || att.emailAddress?.address}
                  {att.status?.response && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 11,
                        color:
                          att.status.response === "accepted"
                            ? "#4caf50"
                            : att.status.response === "declined"
                            ? "#d32f2f"
                            : "#ff9800",
                      }}
                    >
                      (
                      {att.status.response === "accepted"
                        ? "aceito"
                        : att.status.response === "declined"
                        ? "recusado"
                        : att.status.response === "tentativelyAccepted"
                        ? "talvez"
                        : att.status.response === "none"
                        ? "pendente"
                        : att.status.response}
                      )
                    </span>
                  )}
                </div>
              ))}
              {event.attendees.length > 5 && (
                <div
                  style={{ fontSize: 12, marginTop: 2, fontStyle: "italic" }}
                >
                  +{event.attendees.length - 5} participantes
                </div>
              )}
            </div>
          </S.EventDetailRow>
        )}

        {event.bodyPreview && (
          <S.EventDetailRow>
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                color: "#70757a",
                maxHeight: 60,
                overflow: "hidden",
              }}
            >
              {event.bodyPreview}
            </div>
          </S.EventDetailRow>
        )}
      </S.EventDetailBody>

      <S.EventDetailFooter>
        {!event.isCancelled && !isOrganizer && (
          <>
            <Tooltip title="Aceitar">
              <S.EventDetailActionButton
                $color="#4caf50"
                onClick={() => onAccept(event.id)}
              >
                <MdCheck size={16} />
              </S.EventDetailActionButton>
            </Tooltip>
            <Tooltip title="Talvez">
              <S.EventDetailActionButton
                $color="#ff9800"
                onClick={() => onTentative(event.id)}
              >
                <MdHelpOutline size={16} />
              </S.EventDetailActionButton>
            </Tooltip>
            <Tooltip title="Recusar">
              <S.EventDetailActionButton
                $color="#d32f2f"
                onClick={() => onDecline(event.id)}
              >
                <MdClose size={16} />
              </S.EventDetailActionButton>
            </Tooltip>
          </>
        )}

        <Tooltip title="Editar">
          <S.EventDetailActionButton onClick={() => onEdit(event)}>
            <MdEdit size={16} />
          </S.EventDetailActionButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <S.EventDetailActionButton
            $color="#d32f2f"
            onClick={() => onDelete(event.id)}
          >
            <MdDelete size={16} />
          </S.EventDetailActionButton>
        </Tooltip>

        {event.webLink && (
          <S.EventDetailLink
            href={event.webLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 8 }}
          >
            Abrir no Outlook
          </S.EventDetailLink>
        )}
      </S.EventDetailFooter>
    </S.EventDetailPopup>
  );
};

export default EventDetailPopup;
